import download from 'downloadjs';
import Fuse from 'fuse.js';
import { flatten, indexOf, isArray, isEqual, isNull, isUndefined, toArray } from 'lodash/fp';
import { toast } from 'react-toastify';
import { push } from 'redux-first-history';
import { all, call, debounce, delay, fork, put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';

import { addNewClientTag, addNewSystemTag, getAllClientTags as getAllClientTagsServices, saveClause } from '../../../../services/clause';
import { fetchAllDropdownLists } from '../../../../services/dropdownList';
import { fetchLinkedOpinions } from '../../../../services/opinion';
import { UpsertSignOffRequest, fetchOpinionClauses, fetchOpinionInstance, fetchOpinionSignOffInstance, fetchTimelineOpinionInstances, linkClauseToOpinion, opinionInstanceExistsForOpinionId, upsertOpinionInstance, upsertSignOffOpinionInstance, downloadAIExtractionSummary, downloadUpdatedFieldsReport } from '../../../../services/opinionInstance';
import { DropdownListDB } from '../../../admin/dropdown-lists/store/types';
import { SmileyIndicator } from '../../../admin/risk-tolerance/store/types';
import { getIsAdmin } from '../../../auth/login/store/selectors';
import { getAllClientTags } from '../../../clause-library/store/selectors';
import { Clause, ClauseTagDropdownOptions, ClauseTags, ClientTag, FuzzyMatchTags } from '../../../clause-library/store/types';
import { initialClause } from '../../../constants/clauseLibrary';
import { getPositiveAndNegativeAnswers } from '../../../constants/opinion';
import { setIsCheckingHighlightIndexes } from '../../../documents/document-viewer/store/actions';
import { getDocumentViewerType, getPdfText } from '../../../documents/document-viewer/store/selectors';
import { DocumentViewerType, PdfActionTypes, PdfPageText } from '../../../documents/document-viewer/store/types';
import { getHighlightIndexesForPageText } from '../../../documents/document-viewer/store/utils';
import { scopeFormatter } from '../../../shared/table/arkTableFormatters';
import { ColumnSort, TableFilters } from '../../../shared/table/types';
import { clearSourceOpinion, openAdditionalOpinionsStarted, openOpinionAndInstance, openSourceOpinionStarted, setLinkedOpinions, setOpinionSummaryGenerating } from '../../my-opinions/store/actions';
import { getOpinion, getOpinionId } from '../../my-opinions/store/selectors';
import { ArkOpinion, LinkedOpinion } from '../../my-opinions/store/types';
import { addOpinionClauseClientTagFailed, addOpinionClauseClientTagStarted, addOpinionClauseClientTagSuccessful, addOpinionClauseSystemTagFailed, addOpinionClauseSystemTagStarted, addOpinionClauseSystemTagSuccessful, downloadUpdatedFieldsReportFailed, downloadUpdatedFieldsReportSuccessful, downloadAIExtractionSummaryFailed, downloadAIExtractionSummaryStarted, downloadAIExtractionSummarySuccessful, editOpinionInstance, fetchAllOpinionClausesFailed, fetchAllOpinionClausesStarted, fetchAllOpinionClausesSuccessful, fetchOpinionClauseLibraryDropdownOptionsFailed, fetchOpinionClauseLibraryDropdownOptionsSuccessful, linkOpinionClause, openOpinionInstanceById, openOpinionInstanceFailed, openOpinionInstanceSuccessful, openTimelineOpinionInstanceFailed, openTimelineOpinionInstanceStarted, openTimelineOpinionInstanceSuccessful, saveOpinionClauseFailed, saveOpinionClauseStarted, saveOpinionClauseSuccessful, setBlacklineOpinionInstance, setAIAnswerHighlightIndexes, setFieldsUpdated, setInstanceUpdated, setOpinionClauseCanSave, setOpinionSignOff, setSignOffInstanceUpdated, setTimeline, setTimelineInstance, updateAllOpinionClauseClientTags, updateDropdownDetailsContent, updateDropdownFieldValue, updateOpinionClauseFuzzyMatchResults, updateOpinionInstance, updateSmileyContent, upsertOpinionInstanceFailed, upsertOpinionInstanceStarted, upsertOpinionInstanceSuccessful, upsertSignOffOpinionInstanceFailed, upsertSignOffOpinionInstanceStarted, upsertSignOffOpinionInstanceSuccessful } from './actions';
import { getCurrentInstance, getCurrentInstanceType, getCurrentOpinionClause, getCurrentSignOffNotes, getInstanceUpdated, getIsFirstTimelineEntry, getIsEditing, getIsSaving, getIsUpdating, getNettingEngineFields, getOpinionClause, getOpinionClauseColumnSort, getOpinionClauseFilters, getOpinionClauseLibraryDropdownOptions, getOpinionClauseTagTerm, getOpinionClausesPageNumber, getPreviousInstance, getSavedInstance, getSavedSignOffNotes, getSectionOrFieldId } from './selectors';
import { NettingInstance, OpinionFieldPageRef, OpinionInstance, OpinionInstanceActionTypes, OpinionInstanceField, OpinionInstanceType, RepoInstance, SignOffOpinionInstance, TimelineOpinionInstance, UpdatedOpinionField } from './types';
import { compareInstanceFields, compareSignOffNotesFields, updateFieldsUpdated } from './utils';
import { getClauseLibraryDropdownLists, getTagDropdownCategory, stripDropdownList } from '../../../clause-library/store/sagas';
import { dropdownOptionsByTagType } from '../../../clause-library/store/reducer';
import { fetchClauseLibraryDropdownOptionsStarted } from '../../../clause-library/store/actions';
import { MimeType } from '../../../constants/MimeType';
import { calculateBlacklineInstance } from './calculateBlacklineInstance';
import { formatDate } from '../../../../utils/luxon';

const getInstance = (allInstances: TimelineOpinionInstance[], opinionId: number): TimelineOpinionInstance => allInstances.find(timeline => timeline.opinionId === opinionId)!;
const getPreviousOpinionInstance = (allInstances: TimelineOpinionInstance[], selectedInstance: TimelineOpinionInstance): TimelineOpinionInstance | null => {
    let previousOpinionInstance: TimelineOpinionInstance | null = null;
    const timelineOpinionIds = allInstances.map(({ opinionId }) => opinionId);
    const indexOfSelectedInstance = indexOf(selectedInstance.opinionId, timelineOpinionIds);
    if (indexOfSelectedInstance > 0) {
        previousOpinionInstance = allInstances[indexOfSelectedInstance - 1];
    }
    return previousOpinionInstance;
};

export function* attemptOpenOpinionTimelineInstance({ payload }: ReturnType<typeof openTimelineOpinionInstanceStarted>) {
    try {
        let previousOpinionInstance: OpinionInstance | null = null;
        const opinionInstance: OpinionInstance = yield call(fetchOpinionInstance, { opinionInstance: payload });
        const { opinionId, additionalOpinionIds, sourceOpinionId } = opinionInstance;
        const timeline: TimelineOpinionInstance[] = yield call(fetchTimelineOpinionInstances, { opinionId });
        const previousTimelineInstance = getPreviousOpinionInstance(timeline, payload);
        if (!isNull(previousTimelineInstance)) {
            previousOpinionInstance = yield call(fetchOpinionInstance, { opinionInstance: previousTimelineInstance });
        }
        const linkedOpinions: LinkedOpinion[] = yield call(fetchLinkedOpinions, { opinionId });
        yield put(fetchAllOpinionClausesStarted(opinionId, 1));
        yield put(push(`/opinions/analysis/${opinionId}`));
        yield put(setLinkedOpinions(linkedOpinions));
        yield put(openAdditionalOpinionsStarted(additionalOpinionIds));
        if (!isNull(sourceOpinionId)) {
            yield put(openSourceOpinionStarted(sourceOpinionId));
        }
        yield put(openTimelineOpinionInstanceSuccessful(opinionInstance, previousOpinionInstance));
    } catch (e) {
        yield put(openTimelineOpinionInstanceFailed((e as Error).message));
    }
}

export function* attemptOpenOpinionInstance() {
    try {
        const opinionId: number = yield select(getOpinionId);
        const timeline: TimelineOpinionInstance[] = yield call(fetchTimelineOpinionInstances, { opinionId });
        yield put(setTimeline(timeline));
        const opinionInstance = getInstance(timeline, opinionId);
        const opinionSignOff: SignOffOpinionInstance = yield call(fetchOpinionSignOffInstance, { opinionInstance });
        yield put(setOpinionSignOff(opinionSignOff));
        yield put(clearSourceOpinion());
        yield put(openTimelineOpinionInstanceStarted(opinionInstance));
        yield put(openOpinionInstanceSuccessful());
    } catch (e) {
        yield put(openOpinionInstanceFailed((e as Error).message));
    }
}

export function* attemptOpenOpinionInstanceByInstanceId({ payload }: ReturnType<typeof openOpinionInstanceById>) {
    try {
        const opinionId: number = yield select(getOpinionId);
        const { instanceExists }: { instanceExists: boolean } = yield call(opinionInstanceExistsForOpinionId, { opinionInstanceId: payload, opinionId });
        if (!instanceExists) {
            yield put(push('/opinions/my-opinions/map'));
            toast.error('Could not find an instance matching that ID. Please try again.');
        }
        const timeline: TimelineOpinionInstance[] = yield call(fetchTimelineOpinionInstances, { opinionId });
        const opinionInstance: TimelineOpinionInstance = timeline.find(({ opinionInstanceId }) => opinionInstanceId === parseInt(payload))!;
        yield put(setTimeline(timeline));
        yield put(clearSourceOpinion());
        yield put(openTimelineOpinionInstanceStarted(opinionInstance));
        yield put(openOpinionInstanceSuccessful());
    } catch (e) {
        yield put(push('/home'));
        toast.error('Unable to open dataset. Please try again.');
    }
}

export function* attemptSetTimelineInstance({ payload }: ReturnType<typeof setTimelineInstance>) {
    try {
        const { opinionId } = payload;
        yield put(openOpinionAndInstance(opinionId.toString()));
    } catch (e) {
        yield put(push('/home'));
        toast.error('Something went wrong. Please try again.');
    }
}

const getShouldUpdateNettingAnalysis = (updatedFields: UpdatedOpinionField[], nettingEngineFields: string[]) => {
    const updatedSectionFields = updatedFields.map(({ fieldId, sectionId }) => `${sectionId}-${fieldId}`);
    return updatedSectionFields.some(updatedSectionField => nettingEngineFields.includes(updatedSectionField));
};

export function* saveOpinionInstance({ payload }: ReturnType<typeof upsertOpinionInstanceStarted>) {
    try {
        const { isDraft, showToastMessage } = payload;
        const currentInstance: OpinionInstance = yield select(getCurrentInstance);
        const isAdminUser: boolean = yield select(getIsAdmin);
        const isUpdating: boolean = yield select(getIsUpdating);
        const isUpdate = isUpdating || (isAdminUser && currentInstance.isDraft === 0);
        const savedInstance: OpinionInstance = yield select(getSavedInstance);
        const { opinionInstance, updatedFields } = updateFieldsUpdated(currentInstance, savedInstance, isUpdate, isDraft);
        const nettingEngineFields: string[] = yield select(getNettingEngineFields);
        const previousOpinionInstance: OpinionInstance | null = yield select(getPreviousInstance);
        const instanceHasUpdatedNettingEngineFields = getShouldUpdateNettingAnalysis(updatedFields, nettingEngineFields);
        yield put(setOpinionSummaryGenerating(true));
        const { newInstanceId, instance }: { newInstanceId: string, instance: OpinionInstance } = yield call(upsertOpinionInstance, { opinionInstance, isUpdate, isDraft, instanceHasUpdatedNettingEngineFields });
        const timeline: TimelineOpinionInstance[] = yield call(fetchTimelineOpinionInstances, { opinionId: opinionInstance.opinionId });
        yield put(setTimeline(timeline));
        yield put(upsertOpinionInstanceSuccessful(instance, previousOpinionInstance));
        if (showToastMessage) {
            toast('Saved successfully');
        }
        if (!payload || (newInstanceId !== currentInstance.opinionInstanceId.toString())) {
            yield put(openOpinionInstanceById(newInstanceId));
        }
    } catch (e) {
        yield put(upsertOpinionInstanceFailed((e as Error).message));
        yield put(setOpinionSummaryGenerating(false));
        yield call(attemptOpenOpinionInstance);
        toast.error('Unable to update opinion instance.');
    }
}

export function* checkInstanceUpdated() {
    try {
        const isSaving: boolean = yield select(getIsSaving);
        const isAdminUser: boolean = yield select(getIsAdmin);
        const isUpdating: boolean = yield select(getIsUpdating);
        const currentInstance: OpinionInstance | null = yield select(getCurrentInstance);
        const savedInstance: OpinionInstance | null = yield select(getSavedInstance);
        if (!isSaving && !isNull(currentInstance) && !isNull(savedInstance)) {
            const isUpdate = isUpdating || (isAdminUser && currentInstance.isDraft === 0);
            const { updatedFields, unchangedFields } = compareInstanceFields(currentInstance, savedInstance, isUpdate);
            const instanceUpdated = !!updatedFields.length;
            yield put(setFieldsUpdated([...unchangedFields, ...updatedFields]));
            yield put(setInstanceUpdated(instanceUpdated));
        }
    } catch (e) {
        toast.error('We encountered a problem checking if you had made any changes.');
    }
}

export function* resetInstance({ payload }: ReturnType<typeof updateOpinionInstance | typeof editOpinionInstance>) {
    try {
        if (!payload) {
            const savedInstance: OpinionInstance = yield select(getSavedInstance);
            const previousInstance: OpinionInstance | null = yield select(getPreviousInstance);
            yield put(openTimelineOpinionInstanceSuccessful(savedInstance, previousInstance));
        }
    } catch (e) {
        toast.error('Error');
    }
}

export function* checkMLAnswerHighlights() {
    try {
        const documentViewerType: DocumentViewerType | null = yield select(getDocumentViewerType);
        if (documentViewerType === DocumentViewerType.OPINION_ANALYSIS) {
            yield put(setIsCheckingHighlightIndexes(true));
            const opinionPageText: PdfPageText[] = yield select(getPdfText);
            const opinionInstance: OpinionInstance | null = yield select(getCurrentInstance);
            if (!isNull(opinionInstance)) {
                const pageRefData: OpinionFieldPageRef[] = Object.entries(opinionInstance.content).reduce((fieldPageRefs: OpinionFieldPageRef[], [sectionId, fields]) => {
                    Object.entries(fields).forEach(([fieldId, fieldContent]) => {
                        if (isArray(fieldContent)) {
                            (fieldContent as OpinionInstanceField[]).forEach((field, fieldIndex) => {
                                field.pageRef.forEach((pageRef, index) => fieldPageRefs.push({ pageRef, index, fieldIndex, fieldId, sectionId }));
                            });
                        } else {
                            (fieldContent as OpinionInstanceField).pageRef.forEach((pageRef, index) => fieldPageRefs.push({ pageRef, index, fieldId, sectionId }));
                        }
                    });
                    return fieldPageRefs;
                }, []);
                const answersWithoutHighlightIndexes = pageRefData.filter(({ pageRef: { highlightIndexes, page, snippet } }) => ((isUndefined(highlightIndexes) || highlightIndexes.length === 0) && page.length > 0 && !isUndefined(snippet) && snippet.length > 0));

                for (const pageRefData of answersWithoutHighlightIndexes) {
                    const { fieldId, sectionId, fieldIndex, index, pageRef } = pageRefData;
                    const pageText = opinionPageText.find(({ pageNumber }) => pageNumber === parseInt(pageRef.page));
                    if (pageText && !isUndefined(pageRef.snippet)) {
                        const { uniqueIndexes } = getHighlightIndexesForPageText(pageRef.snippet!, pageText);
                        yield put(setAIAnswerHighlightIndexes(sectionId, fieldId, uniqueIndexes, index, fieldIndex));
                    }
                }
                yield delay(1500);
                yield put(setIsCheckingHighlightIndexes(false));
            }
        }
    } catch (e) {
        toast.error('We encountered a problem checking where the AI answers were found.');
    }
}

export function* checkInstanceShouldSave({ payload }: ReturnType<typeof setIsCheckingHighlightIndexes>) {
    const documentViewerType: DocumentViewerType | null = yield select(getDocumentViewerType);
    if (!payload && documentViewerType === DocumentViewerType.OPINION_ANALYSIS) {
        const isEditing: boolean = yield select(getIsEditing);
        const hasUpdated: boolean = yield select(getInstanceUpdated);
        const currentInstance: OpinionInstance | null = yield select(getCurrentInstance);
        if (!isEditing && hasUpdated && !isNull(currentInstance)) {
            yield put(upsertOpinionInstanceStarted(currentInstance.isDraft === 1, false));
        }
    }
}

function* pageHighlightIndexesUpdatedWatcher() {
    yield takeEvery(PdfActionTypes.SET_IS_CHECKING_HIGHLIGHT_INDEXES, checkInstanceShouldSave);
}

function* checkMLAnswerHighlightsWatcher() {
    yield debounce(1000, PdfActionTypes.SET_PDF_TEXT, checkMLAnswerHighlights);
}

function* openTimelineInstanceWatcher() {
    yield takeLatest(OpinionInstanceActionTypes.OPEN_TIMELINE_INSTANCE_STARTED, attemptOpenOpinionTimelineInstance);
}

function* setTimelineInstanceWatcher() {
    yield takeLatest(OpinionInstanceActionTypes.SET_TIMELINE_INSTANCE, attemptSetTimelineInstance);
}

function* openOpinionInstanceWatcher() {
    yield takeLeading(OpinionInstanceActionTypes.OPEN_OPINION_INSTANCE_STARTED, attemptOpenOpinionInstance);
}

function* openOpinionInstanceByInstanceIdWatcher() {
    yield takeLeading(OpinionInstanceActionTypes.OPEN_OPINION_INSTANCE_BY_ID, attemptOpenOpinionInstanceByInstanceId);
}

function* upsertOpinionInstanceWatcher() {
    yield takeLeading(OpinionInstanceActionTypes.UPSERT_OPINION_INSTANCE_STARTED, saveOpinionInstance);
}

function* fieldUpdatedWatcher() {
    yield debounce(500, [
        OpinionInstanceActionTypes.UPDATE_FIELD_VALUE,
        OpinionInstanceActionTypes.UPDATE_TABLE_CELL_FIELD_VALUE,
        OpinionInstanceActionTypes.UPDATE_DROPDOWN_FIELD_VALUE,
        OpinionInstanceActionTypes.UPDATE_WYSIWYG_FIELD_VALUE,
        OpinionInstanceActionTypes.UPDATE_FIELD_REF,
        OpinionInstanceActionTypes.UPDATE_FIELD_PAGE_REF,
        OpinionInstanceActionTypes.UPDATE_FIELD_PAGE_REF_VERIFIED,
        OpinionInstanceActionTypes.ADD_FIELD_PAGE_REF,
        OpinionInstanceActionTypes.REMOVE_FIELD_PAGE_REF,
        OpinionInstanceActionTypes.UPDATE_SINGLE_TOGGLE_FIELD_VALUE,
        OpinionInstanceActionTypes.UPDATE_COUNTERPARTY_COVERAGE_FIELD_VALUE,
        OpinionInstanceActionTypes.UPDATE_CUSTOM_TOGGLE_LABEL,
        OpinionInstanceActionTypes.UPDATE_INCLUDED_INSTANCE_SUB_COUNTERPARTY_TYPES,
        OpinionInstanceActionTypes.UPDATE_TRANSACTION_COVERAGE_FIELD_VALUE,
        OpinionInstanceActionTypes.SET_AI_ANSWER_HIGHLIGHT_INDEXES
    ], checkInstanceUpdated);
}

function* cancelInstanceWatcher() {
    yield takeEvery([OpinionInstanceActionTypes.UPDATE_OPINION_INSTANCE, OpinionInstanceActionTypes.EDIT_OPINION_INSTANCE], resetInstance);
}

export function* attemptCheckCounterpartyUpdateChangesAETRequired() {
    const instance: OpinionInstance | null = yield select(getCurrentInstance);
    const instanceType: OpinionInstanceType | undefined = yield select(getCurrentInstanceType);
    if (isNull(instance) || isUndefined(instanceType)) {
        return;
    }
    const counterpartyCoverageSection = instance.content.counterpartyCoverage;
    const { standardOptions } = counterpartyCoverageSection;
    const allConsideredOptions = standardOptions.filter(({ value }) => value[0]);
    // Do not update the AET required for collateral instances - TODO, factor in client reliance once this is added
    if (![OpinionInstanceType.COLLATERAL_PROVIDER, OpinionInstanceType.COLLATERAL_TAKER].includes(instanceType)) {
        const aetRequiredField = instanceType === OpinionInstanceType.NETTING ?
            (instance.content as NettingInstance).earlyTermination.aetRequired :
            (instance.content as RepoInstance).earlyCloseOut.aetRequired;
        // Check how many of the considered options require AET
        const numberRequiringAET = allConsideredOptions.filter(({ value }) => value[1]).length;
        let calculatedAETAnswer = ['No'];
        if (numberRequiringAET === allConsideredOptions.length && allConsideredOptions.length > 0) {
            calculatedAETAnswer = ['Yes'];
        } else if (numberRequiringAET > Math.floor(allConsideredOptions.length / 2)) {
            calculatedAETAnswer = ['Yes - with exceptions'];
        } else if (numberRequiringAET > 0) {
            calculatedAETAnswer = ['No - with exceptions'];
        }
        // If the calculated answer is different from the current answer, update it
        if (!isEqual(aetRequiredField.dropdownValue, calculatedAETAnswer)) {
            const sectionId = instanceType === OpinionInstanceType.NETTING ? 'earlyTermination' : 'earlyCloseOut';
            yield put(updateDropdownFieldValue(sectionId, 'aetRequired', calculatedAETAnswer));
        }
    }
}

function* checkCounterpartyUpdateChangesAETRequiredWatcher() {
    yield takeEvery(OpinionInstanceActionTypes.UPDATE_COUNTERPARTY_COVERAGE_FIELD_VALUE, attemptCheckCounterpartyUpdateChangesAETRequired);
}

export function* attemptFetchAllOpinionClauses({ payload }: ReturnType<typeof fetchAllOpinionClausesStarted>) {
    try {
        const { opinionId, pageNumber } = payload;
        if (!isUndefined(opinionId)) {
            const filters: TableFilters = yield select(getOpinionClauseFilters);
            const columnSort: ColumnSort | undefined = yield select(getOpinionClauseColumnSort);
            const { clauses, total, allOpinionClauseIds, additionalProvisionsUpdated }: { clauses: Clause[]; total: number; allOpinionClauseIds: number[]; additionalProvisionsUpdated: boolean; } = yield call(fetchOpinionClauses, { opinionId, pageNumber, filters, columnSort });
            yield put(fetchAllOpinionClausesSuccessful(clauses, total, pageNumber, allOpinionClauseIds, additionalProvisionsUpdated));
        }
    } catch (e) {
        yield put(fetchAllOpinionClausesFailed((e as Error).message));
        toast.error('Unable to fetch clauses. Please try again.');
    }
}

function* fetchAllOpinionClausesWatcher() {
    yield takeLatest(OpinionInstanceActionTypes.FETCH_ALL_OPINION_CLAUSES_STARTED, attemptFetchAllOpinionClauses);
}

export function* attemptLinkOpinionClause({ payload }: ReturnType<typeof linkOpinionClause>) {
    const { opinionId, clauseId, shouldRemoveLink } = payload;
    try {
        if (!isUndefined(opinionId)) {
            const pageNumber: number = yield select(getOpinionClausesPageNumber);
            const filters: TableFilters = yield select(getOpinionClauseFilters);
            const columnSort: ColumnSort | undefined = yield select(getOpinionClauseColumnSort);
            const { clauses, total, allOpinionClauseIds, additionalProvisionsUpdated }: { clauses: Clause[]; total: number; allOpinionClauseIds: number[]; additionalProvisionsUpdated: boolean; } = yield call(linkClauseToOpinion, { opinionId, clauseId, pageNumber, filters, columnSort, shouldRemoveLink });
            yield put(fetchAllOpinionClausesSuccessful(clauses, total, pageNumber, allOpinionClauseIds, additionalProvisionsUpdated));
            toast(`Clause successfully ${shouldRemoveLink ? 'removed from' : 'linked to'} the opinion.`);
        }
    } catch (e) {
        yield put(fetchAllOpinionClausesFailed((e as Error).message));
        toast.error(`Unable to ${shouldRemoveLink ? 'remove' : 'add'} the clause link. Please try again.`);
    }
}

function* linkOpinionClauseWatcher() {
    yield takeLatest(OpinionInstanceActionTypes.LINK_OPINION_CLAUSE, attemptLinkOpinionClause);
}

export function* attemptSaveOpinionClause({ payload }: ReturnType<typeof saveOpinionClauseStarted>) {
    try {
        const { opinionId, closeModal } = payload;
        const clause: Clause = yield select(getOpinionClause);
        const savedClause: Clause = yield call(saveClause, { clause, opinionId });
        yield put(saveOpinionClauseSuccessful(savedClause, closeModal));
        const pageNumber: number = yield select(getOpinionClausesPageNumber);
        yield put(fetchAllOpinionClausesStarted(opinionId, pageNumber));
        toast('Clause saved successfully.');
    } catch (e) {
        yield put(saveOpinionClauseFailed((e as Error).message));
        toast.error('Unable to save the clause. Please try again.');
    }
}

function* saveOpinionClauseWatcher() {
    yield takeLeading(OpinionInstanceActionTypes.SAVE_OPINION_CLAUSE_STARTED, attemptSaveOpinionClause);
}

export function* attemptCheckOpinionClauseUpdated() {
    try {
        const currentClause: Clause | null = yield select(getCurrentOpinionClause);
        const originalClause: Clause = isNull(currentClause) ? initialClause() : currentClause;
        const clause: Clause = yield select(getOpinionClause);
        const clauseHasTitle = clause.title !== '';
        const clauseUpdated = !isEqual(originalClause, clause);
        const canSave = clauseHasTitle && clauseUpdated;
        yield put(setOpinionClauseCanSave(canSave));
    } catch (e) {
        toast.error('Unable to check if the clause has updated. Please try again.');
    }
}

function* checkOpinionClauseUpdatedWatcher() {
    yield debounce(1000, [OpinionInstanceActionTypes.UPDATE_OPINION_CLAUSE, OpinionInstanceActionTypes.UPDATE_OPINION_CLAUSE_TAGS, OpinionInstanceActionTypes.ADD_OPINION_CLAUSE_SYSTEM_TAG_STARTED], attemptCheckOpinionClauseUpdated);
}

export function* attemptFetchAllOpinionClauseLibraryDropdownLists() {
    try {
        const dropdownLists: DropdownListDB[] = yield call(fetchAllDropdownLists);
        const allClientSpecificTags: ClientTag[] = yield select(getAllClientTags);
        const { clientSpecificTags, dropdownOptions } = getClauseLibraryDropdownLists(dropdownLists, allClientSpecificTags);
        yield put(updateAllOpinionClauseClientTags(clientSpecificTags));
        yield put(fetchOpinionClauseLibraryDropdownOptionsSuccessful(dropdownOptions));
    } catch (e) {
        yield put(fetchOpinionClauseLibraryDropdownOptionsFailed((e as Error).message));
        toast.error('Unable to fetch the latest dropdown list options. Please try again.');
    }
}

function* fetchAllOpinionClauseLibraryDropdownListsWatcher() {
    yield takeLeading(OpinionInstanceActionTypes.FETCH_OPINION_CLAUSE_LIBRARY_DROPDOWN_OPTIONS_STARTED, attemptFetchAllOpinionClauseLibraryDropdownLists);
}

export function* attemptOpinionClauseFuzzyMatch() {
    try {
        const { tags, systemTemplate } = yield select(getOpinionClause);
        const allClauseTags = flatten(toArray(tags)).map(tag => tag.toLowerCase());
        const clauseLibraryDropdownOptions: ClauseTagDropdownOptions = yield select(getOpinionClauseLibraryDropdownOptions);
        const tagTerm: string = yield select(getOpinionClauseTagTerm);
        const getFuzzyMatchesByType = (key: keyof ClauseTags, value: string) => {
            const isSystemTemplate = systemTemplate === 1;
            const fuzzyMatchOptions = {
                includeScore: true,
                threshold: 0.5,
                keys: ['value']
            };
            const entityFuse = new Fuse(dropdownOptionsByTagType(key, clauseLibraryDropdownOptions, isSystemTemplate), fuzzyMatchOptions);
            const fuzzyMatches = entityFuse.search(value);
            return fuzzyMatches.map(({ item, score }) => ({ key, value: item.value, score: score! }));
        };
        const results = Object.keys(tags).map(value => getFuzzyMatchesByType(value as keyof ClauseTags, tagTerm));
        let sortedMatches: FuzzyMatchTags[] = flatten(results).sort((a, b) => a.score! - b.score!);
        sortedMatches = sortedMatches.filter(({ value }) => !allClauseTags.includes(value.toLowerCase())).slice(0, 10);
        yield put(updateOpinionClauseFuzzyMatchResults(sortedMatches));
    } catch (e) {
        toast.error('Unable to find any suggestions. Please try again.');
    }
}

function* opinionClauseFuzzyMatchWatcher() {
    yield debounce(1000, OpinionInstanceActionTypes.CHECK_OPINION_CLAUSE_FUZZY_MATCH, attemptOpinionClauseFuzzyMatch);
}

export function* attemptAddClientClauseTag({ payload }: ReturnType<typeof addOpinionClauseClientTagStarted>) {
    try {
        const currentClientTags: ClientTag[] = yield call(getAllClientTagsServices);
        const isNewTag = !currentClientTags.map(({ tag }) => tag).includes(payload);
        if (isNewTag) {
            const savedClientTags: ClientTag[] = yield call(addNewClientTag, { tag: payload });
            yield put(addOpinionClauseClientTagSuccessful(savedClientTags));
        }
    } catch (e) {
        yield put(addOpinionClauseClientTagFailed((e as Error).message));
        toast.error('Unable to add the tag. Please try again.');
    }
}

function* addClientClauseTagWatcher() {
    yield takeLeading(OpinionInstanceActionTypes.ADD_OPINION_CLAUSE_CLIENT_TAG_STARTED, attemptAddClientClauseTag);
}

export function* attemptAddSystemClauseTag({ payload }: ReturnType<typeof addOpinionClauseSystemTagStarted>) {
    try {
        const { newTag, category, updatedTags } = payload;

        const dropdownLists: DropdownListDB[] = yield call(fetchAllDropdownLists);
        const listToUpdate = dropdownLists.find(({ name }) => name === getTagDropdownCategory(category));
        if (!isUndefined(listToUpdate)) {
            const dropdownList = stripDropdownList(listToUpdate);
            const updatedList = { ...dropdownList, options: [...dropdownList.options, newTag] };
            const { allDropdownLists, allClientTags }: { allDropdownLists: DropdownListDB[]; allClientTags: ClientTag[]; } = yield call(addNewSystemTag, { dropdownList: updatedList, newTag, category });
            const { clientSpecificTags, dropdownOptions } = getClauseLibraryDropdownLists(allDropdownLists, allClientTags);
            yield put(updateAllOpinionClauseClientTags(clientSpecificTags));
            yield put(fetchOpinionClauseLibraryDropdownOptionsSuccessful(dropdownOptions));
            yield put(addOpinionClauseSystemTagSuccessful(category, updatedTags));
            yield put(fetchClauseLibraryDropdownOptionsStarted());
        }
    } catch (e) {
        yield put(addOpinionClauseSystemTagFailed((e as Error).message));
        toast.error('Unable to add the tag. Please try again.');
    }
}

function* addSystemClauseTagWatcher() {
    yield takeLeading(OpinionInstanceActionTypes.ADD_OPINION_CLAUSE_SYSTEM_TAG_STARTED, attemptAddSystemClauseTag);
}

export function* attemptUpsertSignOffOpinionInstance({ payload }: ReturnType<typeof upsertSignOffOpinionInstanceStarted>) {
    try {
        const signOffOpinionInstance: SignOffOpinionInstance = yield select(getCurrentSignOffNotes);
        const { clientOpinionInstanceId, opinionInstanceId, content, finalApprovalNotes } = signOffOpinionInstance;
        const reqBody: UpsertSignOffRequest = {
            clientOpinionInstanceId,
            opinionInstanceId,
            content,
            finalApprovalNotes,
            approved: payload
        };
        yield call(upsertSignOffOpinionInstance, reqBody);
        yield call(attemptOpenOpinionInstance);
        yield put(upsertSignOffOpinionInstanceSuccessful());
        payload ? toast('Signed Off Successfully') : toast('Saved Sign Off Notes Successfully');
    } catch (e) {
        yield put(upsertSignOffOpinionInstanceFailed((e as Error).message));
    }
}

function* upsertSignOffOpinionInstanceWatcher() {
    yield takeLatest(OpinionInstanceActionTypes.UPSERT_SIGN_OFF_OPINION_INSTANCE_STARTED, attemptUpsertSignOffOpinionInstance);
}

export function* checkSignOffNotesInstanceUpdated() {
    try {
        const isSaving: boolean = yield select(getIsSaving);
        const currentInstance: SignOffOpinionInstance | null = yield select(getCurrentSignOffNotes);
        const savedInstance: SignOffOpinionInstance | null = yield select(getSavedSignOffNotes);
        if (!isSaving && !isNull(currentInstance) && !isNull(savedInstance)) {
            const { updatedFields } = compareSignOffNotesFields(currentInstance, savedInstance);
            const instanceUpdated = !!updatedFields.length;
            yield put(setSignOffInstanceUpdated(instanceUpdated));
        }
    } catch (e) {
        toast.error('We encountered a problem checking if you had made any changes.');
    }
}

function* signOffNotesUpdatedWatcher() {
    yield debounce(500, [
        OpinionInstanceActionTypes.UPDATE_CURRENT_SIGN_OFF_SMILEY_CONTENT,
        OpinionInstanceActionTypes.UPDATE_CURRENT_SIGN_OFF_CONTENT
    ], checkSignOffNotesInstanceUpdated);
}

export function* attemptDownloadAIExtractionSummary({ payload }: ReturnType<typeof downloadAIExtractionSummaryStarted>) {
    try {
        const aiExtractedSummary: Blob = yield call(downloadAIExtractionSummary, { opinionId: payload });
        const mimeType = MimeType.DOCX;
        const filename = 'AI Generated Opinion Summary';
        yield call(download, aiExtractedSummary, filename, mimeType);
        yield put(downloadAIExtractionSummarySuccessful());
    } catch (e) {
        yield put(downloadAIExtractionSummaryFailed((e as Error).message));
        toast.error('Unable to download AI extraction summary. Please try again.');
    }
}

function* downloadAIExtractionSummaryWatcher() {
    yield takeLatest(OpinionInstanceActionTypes.DOWNLOAD_AI_EXTRACTION_SUMMARY_STARTED, attemptDownloadAIExtractionSummary);
}

// Clause Table pagination
export function* opinionClausePaginationNext() {
    const pageNumber: number = yield select(getOpinionClausesPageNumber);
    const opinion: ArkOpinion = yield select(getOpinion);
    yield put(fetchAllOpinionClausesStarted(opinion.opinionId, pageNumber + 1));
}

function* opinionClausePaginationNextWatcher() {
    yield takeLatest(OpinionInstanceActionTypes.OPINION_CLAUSES_PAGINATION_NEXT, opinionClausePaginationNext);
}

export function* opinionClausePaginationPrevious() {
    const pageNumber: number = yield select(getOpinionClausesPageNumber);
    const opinion: ArkOpinion = yield select(getOpinion);
    yield put(fetchAllOpinionClausesStarted(opinion.opinionId, pageNumber - 1));
}

function* opinionClausePaginationPreviousWatcher() {
    yield takeLatest(OpinionInstanceActionTypes.OPINION_CLAUSES_PAGINATION_PREVIOUS, opinionClausePaginationPrevious);
}

export function* opinionClauseTableConfigUpdated() {
    const opinion: ArkOpinion = yield select(getOpinion);
    yield put(fetchAllOpinionClausesStarted(opinion.opinionId, 1));
}

function* opinionClauseTableConfigUpdatedWatcher() {
    yield takeEvery([OpinionInstanceActionTypes.SET_OPINION_CLAUSES_TABLE_FILTERS, OpinionInstanceActionTypes.CLEAR_OPINION_CLAUSES_TABLE_FILTERS, OpinionInstanceActionTypes.SET_OPINION_CLAUSES_TABLE_COLUMN_SORT], opinionClauseTableConfigUpdated);
}

export function* setDefaultSmileySignOff({ payload }: ReturnType<typeof updateDropdownDetailsContent>) {
    const fieldAndSection: string = yield select(getSectionOrFieldId);
    const [sectionId, fieldId] = fieldAndSection.split('-');
    let smiley: SmileyIndicator = SmileyIndicator.NONE;
    const dropdownValue = !isNull(payload) ? payload[0] : null;

    if (!isNull(dropdownValue)) {
        smiley = SmileyIndicator.NEUTRAL;
        const { positiveAnswers, negativeAnswers } = getPositiveAndNegativeAnswers(sectionId, fieldId);
        const isPositive = !isNull(dropdownValue) && positiveAnswers.includes(dropdownValue);
        const isNegative = !isNull(dropdownValue) && negativeAnswers.includes(dropdownValue);
        if (isPositive) {
            smiley = SmileyIndicator.POSITIVE;
        }
        if (isNegative) {
            smiley = SmileyIndicator.NEGATIVE;
        }
    }
    yield put(updateSmileyContent(smiley));
}

function* signOffDropdownUpdatedWatcher() {
    yield takeLeading(OpinionInstanceActionTypes.UPDATE_CURRENT_SIGN_OFF_DROPDOWN_VALUE, setDefaultSmileySignOff);
}

export function* attemptDownloadUpdatedFieldsReport() {
    try {

        const isFirstTimelineEntry: boolean = yield select(getIsFirstTimelineEntry);
        const opinion: ArkOpinion | null = yield select(getOpinion);
        if (!isFirstTimelineEntry && !isNull(opinion)) {
            const { opinionId, scope, bespoke, dateOfOpinion, jurisdiction } = opinion;
            const report: Blob = yield call(downloadUpdatedFieldsReport, { opinionId });
            const filename = `Opinion Updated Fields Report: ${jurisdiction}-${scopeFormatter(scope)}-${bespoke ? 'Bespoke' : 'Industry Standard'}${!isNull(dateOfOpinion) ? `-${formatDate(dateOfOpinion)}` : ''}`;
            yield call(download, report, filename, MimeType.EXCEL);
        }
        yield put(downloadUpdatedFieldsReportSuccessful());
    } catch (e) {
        yield put(downloadUpdatedFieldsReportFailed((e as Error).message));
        toast.error('Unable to download updated changes report. Please try again.');
    }
}

function* downloadUpdatedFieldsReportWatcher() {
    yield takeLeading(OpinionInstanceActionTypes.DOWNLOAD_OPINION_INSTANCE_UPDATED_FIELDS_REPORT_STARTED, attemptDownloadUpdatedFieldsReport);
}

export function* attemptCalculateBlacklineInstance({ payload }: ReturnType<typeof openTimelineOpinionInstanceSuccessful | typeof upsertOpinionInstanceSuccessful>) {
    try {
        const { instance, previousInstance } = payload;
        if (isNull(previousInstance)) {
            yield put(setBlacklineOpinionInstance(null));
            return;
        }
        const blacklineInstance = calculateBlacklineInstance(previousInstance, instance);
        yield put(setBlacklineOpinionInstance(blacklineInstance));
    } catch (e) {
        yield put(setBlacklineOpinionInstance(null));
    }
}

function* calculateBlacklineInstanceWatcher() {
    yield takeLatest([OpinionInstanceActionTypes.OPEN_TIMELINE_INSTANCE_SUCCESSFUL, OpinionInstanceActionTypes.UPSERT_OPINION_INSTANCE_SUCCESSFUL], attemptCalculateBlacklineInstance);
}

export function* opinionInstanceSaga() {
    yield all([
        fork(openTimelineInstanceWatcher),
        fork(openOpinionInstanceWatcher),
        fork(openOpinionInstanceByInstanceIdWatcher),
        fork(upsertOpinionInstanceWatcher),
        fork(fieldUpdatedWatcher),
        fork(cancelInstanceWatcher),
        fork(cancelInstanceWatcher),
        fork(fetchAllOpinionClausesWatcher),
        fork(opinionClausePaginationNextWatcher),
        fork(opinionClausePaginationPreviousWatcher),
        fork(opinionClauseTableConfigUpdatedWatcher),
        fork(saveOpinionClauseWatcher),
        fork(checkOpinionClauseUpdatedWatcher),
        fork(fetchAllOpinionClauseLibraryDropdownListsWatcher),
        fork(opinionClauseFuzzyMatchWatcher),
        fork(linkOpinionClauseWatcher),
        fork(setTimelineInstanceWatcher),
        fork(addSystemClauseTagWatcher),
        fork(addClientClauseTagWatcher),
        fork(upsertSignOffOpinionInstanceWatcher),
        fork(signOffNotesUpdatedWatcher),
        fork(signOffDropdownUpdatedWatcher),
        fork(checkMLAnswerHighlightsWatcher),
        fork(pageHighlightIndexesUpdatedWatcher),
        fork(checkCounterpartyUpdateChangesAETRequiredWatcher),
        fork(downloadAIExtractionSummaryWatcher),
        fork(downloadUpdatedFieldsReportWatcher),
        fork(calculateBlacklineInstanceWatcher)
    ]);
}
